import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import Immutable from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Dropdown, DropdownMenuItem, DropdownMenuDivider } from '@bibliocommons/base-dropdown';
import { accountShape, checkedOutItemShape, bibShape } from '@bibliocommons/bc-prop-types';
import { TransactionalSolidButton } from '@bibliocommons/base-buttons';
import { Link } from '@bibliocommons/base-links';
import ReturnDigitalCheckoutsModal from 'app/components/shared/ItemTransactions/components/digital-checkouts/ReturnDigitalCheckoutsModal';

import DownloadButton from './DownloadButton';
import DownloadModal from './DownloadModal';
import DigitalReturnButton from './DigitalReturnButton';
import ThirdPartyReaderLink from './ThirdPartyReaderLink';

import './CheckedOutDropdown.scss';

function renderViewAllOption() {
  return (
    <DropdownMenuItem>
      <Link href="/v2/checkedout" dataKey="view-all-checkedout">
        <FormattedMessage id="view_all_checked_out" />
      </Link>
    </DropdownMenuItem>
  );
}

export default class CheckedOutDropdown extends React.PureComponent {
  getDownloadModalKey() {
    return `download-modal-${this.props.bib.get('id')}`;
  }

  getDigitalReturnModalKey() {
    return `return-modal-${this.props.bib.get('id')}`;
  }

  renderDownloadModal() {
    const {
      digitalFormats,
      bib,
      checkoutItem,
      isLoading,
      checkoutTransactions,
      currentAccount,
      bibsActions,
      checkoutActions,
      modalActions
    } = this.props;

    const materialPolicy = bib.getIn(['policy', 'materialPolicy'], Immutable.Map());
    if (materialPolicy.get('supportsDownload')) {
      return (
        <DownloadModal
          bib={bib}
          modalKey={this.getDownloadModalKey()}
          bibsActions={bibsActions}
          modalActions={modalActions}
          currentAccount={currentAccount}
          checkoutItem={checkoutItem}
          checkoutTransactions={checkoutTransactions}
          checkoutActions={checkoutActions}
          digitalFormats={digitalFormats}
          isLoading={isLoading}
        />
      );
    }

    return null;
  }

  renderReturnDigitalCheckoutsModal() {
    const { checkoutActions, checkoutItem, checkoutTransactions, currentAccount, bib } = this.props;
    const isReturning = checkoutTransactions.getIn([checkoutItem.get('metadataId'), 'isReturning'], false);
    const checkInEnabled = checkoutItem.get('checkInEnabled');
    const metadataId = checkoutItem.get('metadataId');
    const checkoutId = checkoutItem.get('checkoutId');
    const accountId = currentAccount.get('id');

    if (bib && checkInEnabled) {
      return (
        <ReturnDigitalCheckoutsModal
          bib={bib}
          modalKey={this.getDigitalReturnModalKey()}
          modalActions={this.props.modalActions}
          shelfActions={this.props.shelfActions}
          isLoading={isReturning}
          checkIn={checkoutActions.digitalCheckIn}
          metadataId={metadataId}
          checkoutId={checkoutId}
          accountId={accountId}
        />
      );
    }

    return null;
  }

  renderDigitalReturnOption() {
    const { checkoutItem, modalActions } = this.props;

    return (
      checkoutItem.get('checkInEnabled') && (
        <DropdownMenuItem>
          <DigitalReturnButton modalKey={this.getDigitalReturnModalKey()} modalActions={modalActions} />
        </DropdownMenuItem>
      )
    );
  }

  renderThirdPartyReaderOption() {
    const { checkoutItem } = this.props;

    return (
      checkoutItem.get('thirdPartyReaderUrl') && (
        <DropdownMenuItem>
          <ThirdPartyReaderLink checkoutItem={checkoutItem} />
        </DropdownMenuItem>
      )
    );
  }

  renderDownloadOption() {
    const { bib, checkoutItem, modalActions, digitalFormats } = this.props;

    return digitalFormats.some(format => format.get('hasDownloadLink')) ? (
      <DropdownMenuItem>
        <DownloadButton
          bib={bib}
          modalActions={modalActions}
          modalKey={this.getDownloadModalKey()}
          checkoutItem={checkoutItem}
        />
      </DropdownMenuItem>
    ) : null;
  }

  render() {
    return (
      <div className="cp-checkedout-dropdown">
        <Dropdown
          block
          right
          className="checked-out-dropdown"
          dataKey="checked-out-dropdown"
          buttonTag={TransactionalSolidButton}
          renderLabel={() => <FormattedMessage id="checked_out" />}
        >
          {this.renderDownloadOption()}
          {this.renderThirdPartyReaderOption()}
          {this.renderDigitalReturnOption()}
          <DropdownMenuDivider />
          {renderViewAllOption()}
        </Dropdown>
        {this.renderDownloadModal()}
        {this.renderReturnDigitalCheckoutsModal()}
      </div>
    );
  }
}

CheckedOutDropdown.propTypes = {
  checkoutItem: checkedOutItemShape.isRequired,
  currentAccount: accountShape.isRequired,
  checkoutTransactions: ImmutablePropTypes.map.isRequired,
  bib: bibShape.isRequired,
  isLoading: PropTypes.bool,
  digitalFormats: ImmutablePropTypes.list,
  bibsActions: PropTypes.objectOf(PropTypes.func).isRequired,
  checkoutActions: PropTypes.objectOf(PropTypes.func).isRequired,
  shelfActions: PropTypes.objectOf(PropTypes.func).isRequired,
  modalActions: PropTypes.objectOf(PropTypes.func).isRequired
};
